import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  AlertOctagonIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  File02Icon,
  RefreshCcw01Icon,
} from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';

import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { V2_EVALUATOR_VERSION } from '@/Pages/Settings/utils/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { convertSerializedMessagesToMessageTuple } from '@/components/Rules/RulesEvaluatorPane.utils';
import { cn } from '@/utils/tailwind';

import { useEvaluatorStore } from './store/evaluatorStore';
import { removeFewShotVariable } from './utils/fewShotExamplesUtils';
import {
  EvaluatedEntityType,
  deserializeSchemaToFeedback,
} from './utils/serializeFeedbackUIToLC';
import { useParsedManifest } from './utils/useParsedManifest';
import {
  generateDefaultContent,
  useUpdateFewshotWithFeedbackName,
} from './utils/useUpdateFewshotWithFeedbackName';

// Constants for slider
const MAX_FEW_SHOT_EXAMPLES = 20;

export function EvaluatorFewShotConfig(props: {
  fewShotDatasetId?: string;
  evaluatedEntityType: EvaluatedEntityType;
}) {
  // Access each state item separately to avoid unnecessary re-renders
  const setFewShotEnabled = useEvaluatorStore(
    (state) => state.setFewShotEnabled
  );
  const _setFewShotContent = useEvaluatorStore(
    (state) => state.setFewShotContent
  );
  const setFewShotExamplesMap = useEvaluatorStore(
    (state) => state.setFewShotExamplesMap
  );
  const setNumFewShotExamples = useEvaluatorStore(
    (state) => state.setNumFewShotExamples
  );

  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const fewShotContent = useEvaluatorStore((state) => state.fewShotContent);

  const fewShotExamplesMap = useEvaluatorStore(
    (state) => state.fewShotExamplesMap
  );
  const numFewShotExamples = useEvaluatorStore(
    (state) => state.numFewShotExamples
  );

  const variableMapping = useEvaluatorStore((state) => state.variableMapping);

  const templateFormat = useEvaluatorStore(
    (state) => state.promptTemplateFormat
  );
  const setTemplateFormat = useEvaluatorStore(
    (state) => state.setPromptTemplateFormat
  );

  // Additional store values needed for automatic few-shot removal
  const evaluatorType = useEvaluatorStore((state) => state.evaluatorType);
  const promptMessages = useEvaluatorStore((state) => state.promptMessages);
  const setPromptMessages = useEvaluatorStore(
    (state) => state.setPromptMessages
  );
  const hubRefManifest = useEvaluatorStore((state) => state.hubRefManifest);
  const setHubRefManifest = useEvaluatorStore(
    (state) => state.setHubRefManifest
  );

  const hasNoVariableMappings =
    !variableMapping || Object.keys(variableMapping).length === 0;

  const { manifestStructuredSchema } = useParsedManifest(
    props.evaluatedEntityType
  );
  const deserializedSchema = deserializeSchemaToFeedback(
    manifestStructuredSchema
  );
  const hasReasoning = deserializedSchema?.includeReasoning;
  const hasNotOneFeedbackProperty =
    !manifestStructuredSchema?.properties ||
    Object.keys(manifestStructuredSchema.properties).filter(
      (key) => key !== 'comment'
    ).length !== 1;
  const feedbackName = deserializedSchema?.name ?? 'score';

  const refreshContentRef = useRef<(content: string) => void | null>(
    () => null
  );
  const debouncedFeedbackName = useUpdateFewshotWithFeedbackName(
    props.evaluatedEntityType,
    refreshContentRef
  );

  /*
  The reason we need to keep track of the lastFewShotDefaultContent is:
  
    - if the user turns on few-shot and then edits their template variables/mappings, we do want to update the few shot prompt - but 
        ONLY if they haven't already manually edited the few-shot prompt
    - However, the editing of the variable mappings/template vars is handled by the child RichTextEditor
    - So let's say the user adds a template variable to the main prompt without editing the few-shot template. This gets added to 
        variableMappings in the evaluator store by the child. We want the few shot template to automatically update
    - However, generateDefaultContent now returns a new, updated few-shot template. in the useEffect below we want to update the few shot 
        template - but unless we keep the "previous" state of the default few-shot template, we have no way to know if the user has 
        edited it manually or not, since the default few-shot template is no longer equal to the current few-shot template
    
  The ideal solution is probably to add this logic directly to the setters, but the problem is that the few shot template needs to change
  in a few cases - if they change the variable mapping, if they add/remove template variables, or if they change the feedback key - which 
  are controlled in different places, so that logic would be more complex than it's probably worth.
  */
  const [lastFewShotDefaultContent, setLastFewShotDefaultContent] =
    useState<string>();

  const setFewShotContent = useCallback(
    (content: string) => {
      setLastFewShotDefaultContent(fewShotContent);
      _setFewShotContent(content);
    },
    [_setFewShotContent, fewShotContent]
  );

  const resetFewShotContent = () => {
    const { content, mapping } = generateDefaultContent(
      variableMapping,
      feedbackName,
      hasReasoning
    );

    setFewShotContent(content);
    setFewShotExamplesMap(mapping);
    refreshContentRef.current?.(content);
  };

  // Handler for enabling few-shot with default content
  const handleEnableFewShot = (checked: boolean) => {
    setFewShotEnabled(checked);
    if (checked) {
      resetFewShotContent();
    } else {
      setFewShotContent('');
      setFewShotExamplesMap({});
    }
  };

  const defaultFewShotContent = useMemo(() => {
    return generateDefaultContent(variableMapping, feedbackName, hasReasoning);
  }, [variableMapping, feedbackName, hasReasoning]);

  const hasNotChangedFewShotContent = useMemo(() => {
    return (
      fewShotContent === defaultFewShotContent.content ||
      fewShotContent === lastFewShotDefaultContent
    );
  }, [
    defaultFewShotContent.content,
    fewShotContent,
    lastFewShotDefaultContent,
  ]);

  useEffect(() => {
    if (hasNotChangedFewShotContent) {
      const { content, mapping } = defaultFewShotContent;

      // Create updated mapping that includes the feedback variable
      const updatedMapping = {
        ...mapping,
        [feedbackName]: feedbackName, // Add the feedback name as a valid variable
        few_shot_explanation: 'few_shot_explanation', // Ensure this is always mapped
      };

      // Update mapping first, then content
      setFewShotExamplesMap(updatedMapping);
      setFewShotContent(content);

      // Slight delay to ensure mapping is set before refreshing content
      setTimeout(() => {
        refreshContentRef.current?.(content);
      }, 0);
    }
  }, [
    variableMapping,
    feedbackName,
    hasReasoning,
    hasNotChangedFewShotContent,
    defaultFewShotContent,
    setFewShotExamplesMap,
    setFewShotContent,
  ]);

  // Effect to automatically remove few-shot variable when hasNotOneFeedbackProperty becomes true
  useEffect(() => {
    if (hasNotOneFeedbackProperty && fewShotEnabled) {
      // Disable few-shot first
      setFewShotEnabled(false);

      // Remove few-shot variable from prompt messages
      if (evaluatorType === 'prompt' && promptMessages) {
        const updatedMessages = removeFewShotVariable(promptMessages);
        setPromptMessages(updatedMessages);
      }

      // Remove few-shot variable from hub manifest
      if (
        evaluatorType === 'hub' &&
        hubRefManifest?.kwargs?.first?.kwargs?.messages
      ) {
        try {
          const messages = hubRefManifest.kwargs.first.kwargs.messages;
          const promptMessageTuples =
            convertSerializedMessagesToMessageTuple(messages);
          const updatedPromptMessages =
            removeFewShotVariable(promptMessageTuples);

          // Convert back to manifest format
          const updatedManifest = produce(hubRefManifest, (draft) => {
            draft.kwargs.first.kwargs.messages = updatedPromptMessages.map(
              ([_, messageContent], index) => {
                const originalMessage =
                  draft.kwargs.first.kwargs.messages[index];
                if (originalMessage?.kwargs?.prompt) {
                  originalMessage.kwargs.prompt.kwargs.template =
                    messageContent;
                }
                return originalMessage;
              }
            );
          });

          setHubRefManifest(updatedManifest);
        } catch (_) {
          // If updating the manifest fails continue
        }
      }

      // Clear few-shot content and examples map
      setFewShotContent('');
      setFewShotExamplesMap({});
    }
  }, [
    hasNotOneFeedbackProperty,
    fewShotEnabled,
    evaluatorType,
    promptMessages,
    hubRefManifest,
    setFewShotEnabled,
    setPromptMessages,
    setHubRefManifest,
    setFewShotContent,
    setFewShotExamplesMap,
  ]);

  const availablePaths = useEvaluatorStore((state) => state.availablePaths);
  const promptTemplateFormat = useEvaluatorStore(
    (state) => state.promptTemplateFormat
  );
  const filteredAvailablePaths = useMemo(() => {
    if (promptTemplateFormat === 'f-string') {
      return availablePaths?.filter((path) => !path.includes('.')) ?? [];
    }
    return availablePaths;
  }, [promptTemplateFormat, availablePaths]);

  const variableMappingProps = useMemo(() => {
    return {
      variableMappings: fewShotExamplesMap,
      availablePaths: filteredAvailablePaths,
      setVariableMappings: setFewShotExamplesMap,
      // There's no active variable to set for few-shot mapping
      setActiveVariable: () => void 0,
      uneditableVariables: ['few_shot_explanation', debouncedFeedbackName],
      storeType: 'fewShotExamplesMap' as const,
    };
  }, [
    fewShotExamplesMap,
    setFewShotExamplesMap,
    debouncedFeedbackName,
    filteredAvailablePaths,
  ]);

  const renderTemplateDescription = () => {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger>
            <button
              type="button"
              className="flex items-center gap-1 rounded-md border border-tertiary px-1.5 py-0.5 text-sm text-primary"
            >
              {numFewShotExamples}
              <ChevronDownIcon className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-[200px] overflow-y-auto">
            {Array(MAX_FEW_SHOT_EXAMPLES)
              .fill(null)
              .map((_, i) => (
                <DropdownMenuItem
                  key={i}
                  onClick={() => setNumFewShotExamples(i + 1)}
                >
                  <span>{i + 1}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
        examples will be inserted at{' '}
        <span className="text-brand-green-600">{`{{few_shot_examples}}`}</span>{' '}
        with this template:
      </>
    );
  };

  const hubRef = useEvaluatorStore((state) => state.hubRef);
  const evaluatorVersion = useEvaluatorStore((state) => state.evaluatorVersion);

  const isDisabled =
    hasNoVariableMappings ||
    hasNotOneFeedbackProperty ||
    templateFormat === 'f-string' ||
    (hubRef !== '' && evaluatorVersion <= V2_EVALUATOR_VERSION);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md border border-secondary p-4'
      )}
    >
      <div className="relative flex flex-col">
        <div className="flex w-full items-start justify-stretch">
          <div className="flex items-center justify-center rounded-md bg-brand-green-50 p-1">
            <Checkbox
              checked={fewShotEnabled}
              onChange={(event) => handleEnableFewShot(event.target.checked)}
              component="button"
              disabled={isDisabled}
            />
          </div>
          <div className="flex w-full flex-col gap-3 pl-3">
            <div className="flex flex-col gap-1">
              <Link
                target="_blank"
                to="https://docs.langchain.com/langsmith/create-few-shot-evaluators#how-to-improve-your-evaluator-with-few-shot-examples"
                rel="noopener noreferrer"
                className="flex w-fit cursor-pointer items-center gap-2 self-start hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button type="button" className="text-sm font-semibold">
                  Enable Few-Shot Corrections
                </button>
                <File02Icon className="h-4 w-4 text-placeholder" />
              </Link>
              <p className="text-sm text-tertiary">
                Corrections you make to evaluator scores will automatically be
                used as few-shot examples in future evaluations
              </p>
            </div>

            {fewShotEnabled && (
              <div className="flex items-center gap-1 text-sm text-secondary">
                {renderTemplateDescription()}
              </div>
            )}
            {(templateFormat === 'f-string' ||
              hasNotOneFeedbackProperty ||
              hasNoVariableMappings) && (
              <p
                className={cn(
                  'text-sm',
                  hasNoVariableMappings ? 'text-error' : 'text-warning'
                )}
              >
                {templateFormat === 'f-string' ? (
                  <>
                    Few-shot examples don't support f-string templates.
                    <button
                      type="button"
                      className="ml-2 text-sm text-warning underline"
                      onClick={() => setTemplateFormat('mustache')}
                    >
                      Use mustache
                    </button>
                  </>
                ) : hasNotOneFeedbackProperty ? (
                  'Few-shot examples require exactly one feedback key.'
                ) : hasNoVariableMappings ? (
                  'At least 1 variable is required in the prompt.'
                ) : null}
              </p>
            )}

            {fewShotEnabled && !hasNoVariableMappings && (
              <div className="flex flex-col gap-2 rounded-md border border-secondary bg-background">
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="group flex w-full items-center justify-between gap-2 px-2 py-1 text-sm hover:bg-secondary-hover"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRightIcon
                      className={cn(
                        'h-4 w-4 transition-transform',
                        isOpen && 'rotate-90'
                      )}
                    />
                    <span className=" font-medium">
                      {hasNotChangedFewShotContent
                        ? 'Default Template'
                        : 'Custom Template'}
                    </span>
                    <span className="text-brand-green-600">{`{{few_shot_examples}}`}</span>
                  </div>
                  <button
                    type="button"
                    className={cn(
                      'flex items-center gap-1 rounded-md px-2 py-1 text-sm text-quaternary hover:bg-secondary',
                      hasNotChangedFewShotContent &&
                        'pointer-events-none opacity-0'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetFewShotContent();
                    }}
                  >
                    <RefreshCcw01Icon className="h-4 w-4" /> <span>Reset</span>
                  </button>
                </button>
                {isOpen && (
                  <div
                    className={cn(
                      'flex flex-col gap-2 rounded-md border border-transparent px-3 pb-3',
                      'focus-within:border-brand-green-400 focus-within:border focus-within:bg-background'
                    )}
                  >
                    <div className="flex gap-2 rounded-[5px] bg-secondary p-2">
                      <div className="flex items-center gap-2">
                        <AlertOctagonIcon className="h-4 w-4 text-warning" />
                        <span className="text-sm font-medium">
                          Editing this impacts evaluator behavior
                        </span>
                        <Link
                          to="hhttps://docs.langchain.com/langsmith/create-few-shot-evaluators#how-to-improve-your-evaluator-with-few-shot-examples"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm underline"
                        >
                          <span>Read the docs first </span>
                          <ArrowRightIcon className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                    <RichTextEditor
                      initialValue={fewShotContent}
                      onChange={(content) => {
                        if (typeof content === 'string') {
                          setFewShotContent(content);
                        }
                      }}
                      className="bg-background"
                      variant="template"
                      smallText
                      hideSyntaxHighlighting={false}
                      templateFormat="mustache"
                      refreshContentRef={refreshContentRef}
                      variableMappingProps={variableMappingProps}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
